/**
 * Cloak overlay — keyboard activator.
 *
 * Listens globally for the sequence "gwp74" and pings
 * `/api/crm/cloak-overlay/toggle`. The endpoint returns 404 for any
 * user not on the server-side allowlist (`loic.delobel@gmail.com`), so
 * even if someone else stumbles on the sequence it does nothing.
 *
 * Why a sequence and not a single shortcut: avoids collisions with
 * browser/OS shortcuts and the surrounding app's hotkeys, and keeps
 * the trigger truly obscure (no menu entry to leak).
 *
 * TEMPORARY: removable by deleting this file + the single `<CloakOverlayToggle />`
 * import + mount in `App.tsx`. See
 * `src/app/services/cloakingOverlay/README.md` for the server side.
 */

import { useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';

import { config } from '@/lib/config';
import { setCloakUntil } from '@/lib/cloak-sanitize';

const SEQUENCE = 'gwp74';
const RESET_AFTER_MS = 2_000;

function showToast(text: string, color: string) {
  if (typeof document === 'undefined') return;
  const t = document.createElement('div');
  t.textContent = text;
  Object.assign(t.style, {
    position: 'fixed',
    top: '12px',
    right: '12px',
    padding: '8px 14px',
    background: color,
    color: 'white',
    borderRadius: '6px',
    zIndex: '999999',
    fontFamily: 'system-ui, sans-serif',
    fontSize: '13px',
    boxShadow: '0 4px 12px rgba(0,0,0,.25)',
    pointerEvents: 'none',
  });
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 1500);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function CloakOverlayToggle() {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isSignedIn) return;

    let buffer = '';
    let lastKeyAt = 0;
    let inflight = false;

    const onKey = async (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // Ignore non-printable keys (Tab, Shift, arrows...)
      if (e.key.length !== 1) {
        buffer = '';
        return;
      }

      const now = Date.now();
      if (now - lastKeyAt > RESET_AFTER_MS) buffer = '';
      lastKeyAt = now;

      buffer = (buffer + e.key.toLowerCase()).slice(-SEQUENCE.length);
      if (buffer !== SEQUENCE) return;

      buffer = '';
      if (inflight) return;
      inflight = true;
      try {
        const token = await getToken();
        if (!token) {
          showToast('Cloak: not authenticated', '#dc2626');
          return;
        }
        const r = await fetch(`${config.apiBaseUrl}/api/crm/cloak-overlay/toggle`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          // 404 == not allowed for this user. Silent for everyone else
          // (no toast) so the sequence doesn't reveal anything to
          // non-allowed users who type it by accident.
          if (r.status !== 404) {
            console.log('Cloak toggle failed:', r.status, await r.text());
          }
          return;
        }
        const { enabled, until } = (await r.json()) as { enabled: boolean; until: number | null };
        // Persist the activation flag for the frontend sanitizer
        // (org/store name guard). Cleared on toggle-off and auto-
        // expires from localStorage when `until` passes.
        setCloakUntil(enabled ? until : null);
        showToast(`Cloak overlay: ${enabled ? 'ON' : 'OFF'}`, enabled ? '#16a34a' : '#404040');
        // Reload after a beat so the toast is visible before refetch.
        setTimeout(() => window.location.reload(), 600);
      } catch (err) {
        console.log('Cloak toggle error:', err);
      } finally {
        inflight = false;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSignedIn, getToken]);

  return null;
}
